import { SyntheticEvent, UIEvent, useEffect, useMemo } from 'react'

import { useInfiniteQuery } from '@api/hooks'
import DateRangeMenu from '@components/DateRangeMenu/DateRangeMenu'
import SearchInput from '@components/SearchInput/SearchInput'
import { Box, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import { DateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro'
import DirectDealApi, { DirectDealKeys } from '@services/directDeals.api'
import { DirectDealStatus } from '@services/directDeals.types'
import MarketBasketReportsApi, { MarketBasketReportsKeys } from '@services/marketBasketReports.api'
import {
	DirectDealBasket,
	DirectDealBasketsRequest,
	DirectDealsKpiSummary,
} from '@services/marketBasketReports.types'
import { useQueryClient } from '@tanstack/react-query'
import { isDev, isLocal } from '@utils/EnvironmentHelper'
import {
	defaultMarketBasketDateRanges,
	devMarketBasketDateRanges,
} from '@views/MarketBaskets/MarketBasket.utils'
import dayjs, { Dayjs } from 'dayjs'
import { NumberParam, StringParam, useQueryParam, withDefault } from 'use-query-params'

import BasketCard from './BasketCard'

const showDevRanges = isDev || isLocal
const ranges = [
	...defaultMarketBasketDateRanges,
	...(showDevRanges ? devMarketBasketDateRanges : []),
]

type TabOptions = 'ALL' | 'NOT_ACCEPTED' | 'NOT_REJECTED'

type Props = {
	onSelectBasket: (basket: DirectDealBasket) => void
}

export default function BasketList({ onSelectBasket: onSelect }: Props) {
	const queryClient = useQueryClient()
	const [dateRangeStart, setDateRangeStart] = useQueryParam(
		'dateRangeStart',
		withDefault(StringParam, dayjs().subtract(30, 'day').format('YYYY-MM-DD'))
	)
	const [dateRangeEnd, setDateRangeEnd] = useQueryParam(
		'dateRangeEnd',
		withDefault(StringParam, dayjs().format('YYYY-MM-DD'))
	)
	const [activeTab, setActiveTab] = useQueryParam('market-baskets', withDefault(StringParam, 'ALL'))
	const [search, setSearch] = useQueryParam('search', withDefault(StringParam, ''))
	const [selectedBasketId, setSelectedBasketId] = useQueryParam('basket', NumberParam)
	const [detailTab] = useQueryParam('direct-deals', withDefault(StringParam, 'ALL'))

	const basketsQueryBase: DirectDealBasketsRequest = {
		dateRangeStart,
		dateRangeEnd,
		search,
		category: activeTab as TabOptions,
		pageSize: 100,
	}

	const {
		data: directDealBaskets,
		isLoading,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
	} = useInfiniteQuery({
		queryKey: MarketBasketReportsKeys.directDealBaskets(basketsQueryBase),
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			MarketBasketReportsApi.directDealBaskets({
				...basketsQueryBase,
				page: pageParam,
			}),
		getNextPageParam: (lastPage) => {
			const page = lastPage.pagination?.page ?? 0
			const totalPages = lastPage.pagination?.totalPages ?? 0
			return page < totalPages ? page + 1 : null
		},
		enabled: dayjs(dateRangeStart).isValid() && dayjs(dateRangeEnd).isValid(),
	})

	const baskets = useMemo(
		() => directDealBaskets?.pages.flatMap((page) => page.data as DirectDealBasket[]) ?? [],
		[directDealBaskets]
	)

	const kpiSummary = useMemo<DirectDealsKpiSummary | undefined>(() => {
		const summary = directDealBaskets?.pages[0]?.summary
		if (!summary) return undefined

		const { recommendedAccepted, recommendedTotal, declineRejected, declineTotal } = summary

		return {
			recommendPct: recommendedTotal > 0 ? recommendedAccepted / recommendedTotal : 0,
			reviewPct: 0,
			declinePct: declineTotal > 0 ? declineRejected / declineTotal : 0,
		}
	}, [directDealBaskets])

	// Keep parent selected basket in sync with URL selection; default to first basket when missing.
	useEffect(() => {
		if (baskets.length === 0) {
			setSelectedBasketId(null)
			onSelect(null)
			return
		}

		const selectedBasket = baskets.find(
			(basket) => basket.marketBasketId === Number(selectedBasketId)
		)
		if (selectedBasket) {
			onSelect(selectedBasket)
			return
		}

		const firstBasket = baskets[0]
		setSelectedBasketId(firstBasket.marketBasketId as number)
		onSelect(firstBasket)
	}, [baskets, onSelect, selectedBasketId, setSelectedBasketId])

	// Prefetch next basket detail queries whenever selected basket changes.
	useEffect(() => {
		if (!selectedBasketId || baskets.length === 0) return

		const currentIndex = baskets.findIndex(
			(basket) => basket.marketBasketId === Number(selectedBasketId)
		)
		const nextBasket = baskets[currentIndex + 1]
		if (!nextBasket) return

		queryClient.prefetchQuery({
			queryKey: DirectDealKeys.summary(nextBasket.marketBasketId),
			queryFn: () => DirectDealApi.summary(nextBasket.marketBasketId),
		})

		queryClient.prefetchQuery({
			queryKey: DirectDealKeys.list(nextBasket.marketBasketId, {
				status: detailTab as DirectDealStatus,
			}),
			queryFn: () =>
				DirectDealApi.list(nextBasket.marketBasketId, { status: detailTab as DirectDealStatus }),
		})
	}, [baskets, detailTab, queryClient, selectedBasketId])

	const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
		if (!hasNextPage || isFetchingNextPage) return

		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
		const threshold = 24
		const reachedBottom = scrollHeight - scrollTop - clientHeight <= threshold

		if (reachedBottom) {
			fetchNextPage()
		}
	}

	const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
		setActiveTab(newValue)
	}

	const handleChangeDateRange = ([start, end]: [Dayjs | null, Dayjs | null]) => {
		setDateRangeStart(start ? start.format('YYYY-MM-DD') : undefined)
		setDateRangeEnd(end ? end.format('YYYY-MM-DD') : undefined)
	}

	const handleSelectBasket = (basket: DirectDealBasket) => {
		setSelectedBasketId(basket.marketBasketId as number)
		onSelect(basket)
	}

	const totalCount = directDealBaskets?.pages[0]?.summary.totalBaskets ?? 0

	return (
		<BasketListUI
			isLoading={isLoading}
			baskets={baskets}
			totalCount={totalCount}
			kpiSummary={kpiSummary}
			selectedBasketId={selectedBasketId ?? null}
			activeTab={activeTab as TabOptions}
			search={search ?? ''}
			dateRange={{
				start: dayjs(dateRangeStart),
				end: dayjs(dateRangeEnd),
			}}
			onSelectBasket={handleSelectBasket}
			onChangeDateRange={handleChangeDateRange}
			onTabChange={handleTabChange}
			onSearch={setSearch}
			onListScroll={handleListScroll}
			isFetchingMore={isFetchingNextPage}
		/>
	)
}

type UiProps = {
	isLoading: boolean
	baskets: DirectDealBasket[]
	totalCount: number
	kpiSummary: DirectDealsKpiSummary | undefined
	selectedBasketId: Id | null
	activeTab: TabOptions
	search: string
	dateRange: { start: Dayjs | null; end: Dayjs | null }
	onSelectBasket: (basket: DirectDealBasket) => void
	onChangeDateRange: (value: [Dayjs | null, Dayjs | null]) => void
	onTabChange: (event: SyntheticEvent, newValue: string) => void
	onSearch: (value: string) => void
	onListScroll: (event: UIEvent<HTMLDivElement>) => void
	isFetchingMore: boolean
}

export function BasketListUI({
	isLoading,
	baskets,
	totalCount,
	kpiSummary,
	selectedBasketId,
	activeTab,
	search,
	dateRange,
	onSelectBasket,
	onChangeDateRange,
	onTabChange,
	onSearch,
	onListScroll,
	isFetchingMore,
}: UiProps) {
	return (
		<Paper
			sx={{
				width: 537,
				flexShrink: 0,
				height: '100%',
				minHeight: 0,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 2,
				p: 2,
				display: 'flex',
				flexDirection: 'column',
				gap: 0,
				overflow: 'hidden',
			}}
		>
			<Stack spacing={2} sx={{ pb: 2 }}>
				<Typography variant="h6">Market Baskets ({totalCount})</Typography>

				<DateRangePicker
					label="Date Range"
					onChange={onChangeDateRange}
					value={[dateRange.start, dateRange.end]}
					disabled={isLoading}
					slots={{ field: SingleInputDateRangeField }}
					slotProps={{
						textField: {
							fullWidth: true,
							InputProps: {
								endAdornment: (
									<DateRangeMenu
										ranges={ranges}
										onClick={(e) => onChangeDateRange([e.start, e.end])}
									/>
								),
							} as any,
						},
					}}
					data-testid="date-range-picker"
				/>

				<SearchInput
					size="small"
					placeholder="Search by name or case number..."
					value={search}
					disabled={isLoading}
					searchOnEnter
					onChange={onSearch}
					data-testid="search-input"
				/>

				<Stack direction="row" justifyItems="space-around">
					<KpiCell
						isLoading={isLoading}
						label="Recommend"
						pct={kpiSummary?.recommendPct || 0}
						color="success.main"
					/>
					<KpiCell
						isLoading={isLoading}
						label="Decline"
						pct={kpiSummary?.declinePct || 0}
						color="error.main"
						noBorderRight
					/>
				</Stack>
			</Stack>

			<Box>
				<Tabs value={activeTab} onChange={onTabChange} aria-label="tab filter">
					<Tab label="All" value="ALL" data-testid="tab-all" />
					<Tab label="Not Accepted" value="NOT_ACCEPTED" data-testid="tab-not-accepted" />
					<Tab label="Not Rejected" value="NOT_REJECTED" data-testid="tab-not-rejected" />
				</Tabs>
			</Box>

			<Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, pt: 2 }} onScroll={onListScroll}>
				<List
					isLoading={isLoading}
					baskets={baskets}
					selectedBasketId={selectedBasketId}
					onSelectBasket={onSelectBasket}
				/>
				{isFetchingMore && <Skeleton variant="rounded" width="100%" height="8.625rem" />}
			</Box>
		</Paper>
	)
}

type ListProps = {
	isLoading: boolean
	baskets: DirectDealBasket[]
	selectedBasketId: Id | null
	onSelectBasket: (basket: DirectDealBasket) => void
}

function List({ isLoading, baskets, selectedBasketId, onSelectBasket }: ListProps) {
	if (isLoading) {
		return (
			<Stack spacing={2}>
				{[...Array(3)].map((_, i) => (
					// eslint-disable-next-line react/no-array-index-key
					<Skeleton key={i} variant="rounded" width="100%" height="8.625rem" />
				))}
			</Stack>
		)
	}

	return (
		<Stack spacing={2}>
			{baskets.map((basket) => (
				<BasketCard
					key={basket.marketBasketId}
					basket={basket}
					isSelected={basket.marketBasketId === Number(selectedBasketId)}
					onSelect={onSelectBasket}
					data-testid={`basket-card-${basket.marketBasketId}`}
				/>
			))}
		</Stack>
	)
}

type KpiCellProps = {
	isLoading: boolean
	label: string
	pct: number
	color: string
	noBorderRight?: boolean
}

function KpiCell({ isLoading, label, pct, color, noBorderRight = false }: KpiCellProps) {
	return (
		<Box
			sx={{
				flex: 1,
				py: 1,
				textAlign: 'center',
				borderTop: '1px solid',
				borderBottom: '1px solid',
				borderRight: noBorderRight ? 'none' : '1px solid',
				borderColor: 'divider',
			}}
		>
			<Typography variant="body2" fontWeight={600} color={color} align="center">
				{label}
			</Typography>
			<Stack direction="row" spacing={1} justifyContent="center" alignItems="baseline">
				<Typography
					variant="h6"
					color="text.primary"
					data-testid={`kpi-pct-${label.toLowerCase()}`}
				>
					{isLoading ? (
						<Skeleton sx={{ height: '2rem', width: '2rem' }} />
					) : (
						`${Math.round(pct * 100)}%`
					)}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{label === 'Recommend' ? 'Accepted' : 'Rejected'}
				</Typography>
			</Stack>
		</Box>
	)
}
